import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router";
import { CreateFormKeys } from "../components/ProductComponents/CreateComponentsModal/createConstants";
import { EPartnerValidationProgress, ProductListingTypes } from "../constants/product";
import type { ProductRouteParams } from "../constants/routerconstants";
import type {
	IProductVersionsEntity,
	ISuperGraphProductVersion,
	ProjectListGraphQLResponseDataEntity,
} from "../interfaces/graphql";
import {
	productDetailQueryKey,
	useChecklistStatus,
	useProductDetail,
	useProductScore,
	useSuperGraphProductListing,
} from "../queries/productDetails";
import type { ProductListing } from "../types/generated-graphql";
import {
	extractCommonProjectMetadata,
	type IComponentMetadata,
	validateAttachedProjects,
} from "../utils/attachedProjectValidator";
import { FixProductListingData } from "../utils/fix-data";
import { useBadDataState } from "./useBadDataState";

interface IProductContextComponentMetadata extends IComponentMetadata {
	isValid: boolean;
}

export type TProductDetails = Omit<ProductListing, "edges">;

// Type for partial product updates
export type TProductDetailsUpdate = Partial<Omit<ProductListing, "edges" | "__typename">>;

interface IProductMetadata {
	productDetail: TProductDetails;
	setProductData: (data: TProductDetailsUpdate) => void;
	activeCertificationListState: ProjectListGraphQLResponseDataEntity[];
	certificationListState: ProjectListGraphQLResponseDataEntity[];
	setCertificationListState: (cert: ProjectListGraphQLResponseDataEntity[]) => void;
	isPartnerValidationSupported: boolean; // Flag that indicates if the product type supports partner validation
	componentMetadata: IProductContextComponentMetadata; // Contains info about the attached components
	isProductPartnerValidated: boolean; // Tells if a product is partner validated
	partnerValidationProgress: EPartnerValidationProgress; // Partner Validation progress
	productVersionsData: IProductVersionsEntity[];
	setProductVersionsState: (productVersions: IProductVersionsEntity[]) => void;
	combinedVersionsData: (IProductVersionsEntity | ISuperGraphProductVersion)[]; // combines the partner product versions data with the supergraph functional certifications
	globalDisabledState: boolean; // status of checklist, product score and bad data
}

export const useProductMetadata = (): IProductMetadata => {
	const queryClient = useQueryClient();
	const params = useParams<ProductRouteParams>();
	const badDataState = useBadDataState(params.productId);
	const { isFetching: isProductScoreFetching } = useProductScore(params.productId);
	const { isFetching: isChecklistFetching } = useChecklistStatus(params.productId);
	const globalDisabledState = isProductScoreFetching || isChecklistFetching || badDataState;
	const { data: productDetailOriginal } = useProductDetail(params?.productId);
	const { data: superGraphVersionsData } = useSuperGraphProductListing(params.productId);
	const productDetail = useMemo(() => {
		const omittedValue = {
			...productDetailOriginal,
		};
		delete omittedValue.edges;
		return omittedValue;
	}, [productDetailOriginal]);
	const setProductData = (data: TProductDetailsUpdate) => {
		const newData = FixProductListingData({
			...productDetailOriginal,
			...data,
		});
		queryClient.setQueryData(productDetailQueryKey(params?.productId), newData);
	};
	const {
		certificationListState,
		isPartnerValidationSupported,
		isProductPartnerValidated,
		validationStatus,
		productVersionsData,
	} = useMemo(() => {
		const certificationListState = (productDetailOriginal?.edges?.cert_projects?.data || []).filter(
			(item) => item !== null && item !== undefined,
		) as unknown as ProjectListGraphQLResponseDataEntity[];
		const productVersionsData = (productDetailOriginal?.edges?.product_versions?.data || []).filter(
			(item): item is IProductVersionsEntity => item !== null && item !== undefined,
		);
		const isProductPartnerValidated = productDetailOriginal?.product_validation?.partner_validated === true;
		const validationStatus = isProductPartnerValidated
			? EPartnerValidationProgress.Validated
			: productDetailOriginal?.product_validation != null && productVersionsData?.length > 0
				? EPartnerValidationProgress.Started
				: EPartnerValidationProgress.None;
		const isPartnerValidationSupported =
			[ProductListingTypes.ContainerStack, ProductListingTypes.TraditionalApplication].indexOf(
				productDetailOriginal?.type || "",
			) !== -1;
		return {
			certificationListState,
			isPartnerValidationSupported,
			isProductPartnerValidated,
			validationStatus,
			productVersionsData,
		};
	}, [productDetailOriginal]);
	const setCertificationListState = (newCertList: ProjectListGraphQLResponseDataEntity[]) => {
		// Sorting to match a normal query
		newCertList?.sort((a, b) => {
			return new Date(b?.last_update_date || "").getTime() - new Date(a?.last_update_date || "").getTime();
		});
		const existingCertComponentIds = productDetail?.cert_projects || [];
		const newCertListIds = newCertList?.map((item) => item?._id);
		const certProjectIds = existingCertComponentIds
			? Array.from(new Set([...existingCertComponentIds, ...newCertListIds]))
			: newCertListIds;
		const edges = {
			...(productDetailOriginal?.edges || {}),
			cert_projects: {
				data: [...newCertList],
			},
		};
		const newData = FixProductListingData({
			...productDetailOriginal,
			[CreateFormKeys.certProjects]: certProjectIds,
			edges,
		});
		queryClient.setQueryData(productDetailQueryKey(params?.productId), newData);
	};
	const setProductVersionsState = (newProductVersionList: IProductVersionsEntity[]) => {
		// Sorting to match a normal query
		newProductVersionList?.sort((a, b) => {
			return new Date(b?.creation_date || 0).getTime() - new Date(a?.creation_date || 0).getTime();
		});
		const edges = {
			...(productDetailOriginal?.edges || {}),
			product_versions: {
				data: [...newProductVersionList],
			},
		};

		const newData = FixProductListingData({
			...productDetailOriginal,
			edges,
		});
		queryClient.setQueryData(productDetailQueryKey(params?.productId), newData);
	};
	const activeCertificationListState = useMemo(
		() => certificationListState?.filter((item) => item.project_status === "active"),
		[certificationListState],
	);
	const combinedVersionsData = useMemo(
		() => [...productVersionsData, ...(superGraphVersionsData ?? [])],
		[productVersionsData, superGraphVersionsData],
	);

	return {
		productDetail,
		setProductData,
		certificationListState,
		setCertificationListState,
		isPartnerValidationSupported,
		isProductPartnerValidated,
		partnerValidationProgress: validationStatus,
		productVersionsData,
		setProductVersionsState,
		componentMetadata: {
			...extractCommonProjectMetadata(activeCertificationListState),
			isValid: validateAttachedProjects(productDetail, activeCertificationListState, true),
		},
		globalDisabledState,
		combinedVersionsData,
		activeCertificationListState,
	};
};
